import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type {
  CurveScientificArtifactDocument,
  FlowEvent,
  FlowGate,
  FlowScientificArtifactDocument,
  LocalCurveScientificArtifactDocument,
  LocalFlowScientificArtifactDocument,
  ScientificArtifactDocument,
  ScientificArtifactInspectionDocument,
  ScientificOperationResult,
  ScientificPoint,
  ScientificSelection,
  ScientificSelectionBase,
} from './types';

interface ScientificArtifactStudioProps {
  artifactId: string;
  document: ScientificArtifactInspectionDocument;
  /** Local snapshots without a canonical revision may be selected, not mutated. */
  readOnly?: boolean;
  /** Existing selection, used only to initialize a remounted Flow Studio. */
  selection?: ScientificSelection;
  operation?: ScientificOperationResult;
  onSelectionChange(selection: ScientificSelectionBase): void;
  onApply?(
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ): Promise<ScientificArtifactDocument>;
}

export function ScientificArtifactStudio(props: ScientificArtifactStudioProps) {
  if (props.document.renderer === 'curve') {
    return <CurveStudio {...props} document={props.document} />;
  }
  return <FlowStudio {...props} document={props.document} />;
}

function isCanonicalCurveDocument(
  document: CurveScientificArtifactDocument | LocalCurveScientificArtifactDocument,
): document is CurveScientificArtifactDocument {
  return (
    typeof document.revision === 'number' &&
    Number.isSafeInteger(document.revision) &&
    document.revision >= 1
  );
}

function isCanonicalFlowDocument(
  document: FlowScientificArtifactDocument | LocalFlowScientificArtifactDocument,
): document is FlowScientificArtifactDocument {
  return (
    typeof document.revision === 'number' &&
    Number.isSafeInteger(document.revision) &&
    document.revision >= 1
  );
}

const curveView = {
  width: 720,
  height: 310,
  left: 62,
  right: 24,
  top: 24,
  bottom: 42,
};

function CurveStudio({
  artifactId,
  document,
  readOnly = false,
  operation,
  onSelectionChange,
  onApply,
}: Omit<ScientificArtifactStudioProps, 'document'> & {
  document: CurveScientificArtifactDocument | LocalCurveScientificArtifactDocument;
}) {
  const canonicalDocument = isCanonicalCurveDocument(document)
    ? document
    : undefined;
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionBox, setSelectionBox] = useState<{
    start: { x: number; y: number };
    current: { x: number; y: number };
  }>();
  const [selectionGeometry, setSelectionGeometry] =
    useState<ScientificSelectionBase['geometry']>();
  const [hoveredPoint, setHoveredPoint] = useState<ScientificPoint>();
  const [showReplicates, setShowReplicates] = useState(
    document.state.visible_replicates.length > 0,
  );
  const [dismissedOperation, setDismissedOperation] =
    useState<ScientificOperationResult>();
  const [applyError, setApplyError] = useState<string>();
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setShowReplicates(document.state.visible_replicates.length > 0);
    setDismissedOperation(undefined);
    setApplyError(undefined);
  }, [document.revision, document.state.visible_replicates]);

  const points = useMemo(
    () =>
      showReplicates
        ? document.data.points
        : firstPointPerConcentration(document.data.points),
    [document.data.points, showReplicates],
  );
  const xValues = document.data.points.map((point) => point.dose_nm);
  const yValues = document.data.points.map((point) => point.response_percent);
  const xMin = Math.max(Number.MIN_VALUE, Math.min(...xValues));
  const xMax = Math.max(xMin * 1.01, Math.max(...xValues));
  const canonicalRevision = canonicalDocument?.revision;
  const previewDocument: CurveScientificArtifactDocument | undefined =
    !readOnly &&
    canonicalDocument &&
    operation?.renderer === 'curve' &&
    operation.operation === 'curve.exclude_selected_refit' &&
    operation.selection.artifactId === artifactId &&
    operation.base_revision === canonicalRevision &&
    sameIds(operation.selection.ids, selectedIds) &&
    operation !== dismissedOperation
      ? mergeScientificOperation(canonicalDocument, operation)
      : undefined;
  const yMin = Math.min(
    document.derived.parameters.bottom,
    previewDocument?.derived.parameters.bottom ?? Number.POSITIVE_INFINITY,
    ...yValues,
  );
  const yMax = Math.max(
    yMin + 1,
    document.derived.parameters.top,
    previewDocument?.derived.parameters.top ?? Number.NEGATIVE_INFINITY,
    ...yValues,
  );
  const plotWidth = curveView.width - curveView.left - curveView.right;
  const plotHeight = curveView.height - curveView.top - curveView.bottom;
  const xMapping = document.source.x_mapping;
  const mapCurveDose = (value: number) =>
    curveDoseMappingValue(value, xMapping);
  const scaleX = (value: number) =>
    curveView.left +
    ((mapCurveDose(value) - mapCurveDose(xMin)) /
      (mapCurveDose(xMax) - mapCurveDose(xMin))) *
      plotWidth;
  const scaleY = (value: number) =>
    curveView.top + ((yMax - value) / (yMax - yMin)) * plotHeight;
  const unscaleX = (value: number) =>
    curveDoseMappingInverse(
      mapCurveDose(xMin) +
        ((value - curveView.left) / plotWidth) *
          (mapCurveDose(xMax) - mapCurveDose(xMin)),
      xMapping,
    );
  const unscaleY = (value: number) =>
    yMax - ((value - curveView.top) / plotHeight) * (yMax - yMin);
  const activeFit =
    previewDocument?.derived.parameters ?? document.derived.parameters;
  const committedExcluded = new Set(document.state.excluded_point_ids);
  const selectedSet = new Set(selectedIds);
  const series = [
    ...new Set(
      document.data.points.map((point) => point.series_id ?? 'series'),
    ),
  ].map((id) => ({ id, label: id }));
  const selection: ScientificSelectionBase = {
    renderer: 'curve',
    artifactId,
    ids: selectedIds,
    geometry: selectionGeometry,
    points: curveSelectionPoints(document.data.points, selectedIds),
  };

  useEffect(() => {
    onSelectionChange(selection);
  }, [artifactId, onSelectionChange, selectedIds, selectionGeometry]);

  const selectPoint = (
    pointId: string,
    additive: boolean,
  ) => {
    const next = additive
      ? selectedIds.includes(pointId)
        ? selectedIds.filter((id) => id !== pointId)
        : [...selectedIds, pointId]
      : [pointId];
    setSelectedIds(next);
    setSelectionGeometry(undefined);
  };

  const beginBoxSelection = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 || event.target !== event.currentTarget.querySelector('.curve-hit-plane')) {
      return;
    }
    const point = svgPoint(event, svgRef.current, curveView.width, curveView.height);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectionBox({ start: point, current: point });
  };

  const updateBoxSelection = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!selectionBox) return;
    const point = svgPoint(event, svgRef.current, curveView.width, curveView.height);
    if (point) setSelectionBox({ ...selectionBox, current: point });
  };

  const finishBoxSelection = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!selectionBox) return;
    const point =
      svgPoint(event, svgRef.current, curveView.width, curveView.height) ??
      selectionBox.current;
    const left = Math.min(selectionBox.start.x, point.x);
    const right = Math.max(selectionBox.start.x, point.x);
    const top = Math.min(selectionBox.start.y, point.y);
    const bottom = Math.max(selectionBox.start.y, point.y);
    const ids = points
      .filter((candidate) => {
        const x = scaleX(candidate.dose_nm);
        const y = scaleY(candidate.response_percent);
        return x >= left && x <= right && y >= top && y <= bottom;
      })
      .map((candidate) => candidate.id);
    const nextIds = event.shiftKey
      ? [...new Set([...selectedIds, ...ids])]
      : ids;
    setSelectedIds(nextIds);
    setSelectionGeometry({
      type: 'box',
      xMin: round(unscaleX(left)),
      xMax: round(unscaleX(right)),
      yMin: round(unscaleY(bottom)),
      yMax: round(unscaleY(top)),
    });
    setSelectionBox(undefined);
  };

  const applyPreview = async () => {
    if (!previewDocument || !onApply) return;
    setApplying(true);
    setApplyError(undefined);
    try {
      if (canonicalRevision === undefined) return;
      await onApply(artifactId, canonicalRevision, previewDocument);
      setDismissedOperation(operation);
    } catch (cause) {
      setApplyError(cause instanceof Error ? cause.message : 'Curve update failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <section
      className="scientific-studio curve-studio"
      data-testid="scientific-renderer-curve"
      data-artifact-revision={canonicalRevision}
      data-local-context={readOnly ? 'true' : 'false'}
      data-committed-state={`excluded:${document.state.excluded_point_ids.length}`}
      aria-label="Curve Studio scientific renderer"
    >
      <div className="scientific-toolbar">
        <div>
          <span className="scientific-kicker">Curve Studio / 4PL</span>
          <strong>{document.title ?? 'Dose response curve'}</strong>
        </div>
        <span
          className="scientific-selection-count"
          data-testid="scientific-selection-count"
          aria-live="polite"
        >
          {selectedIds.length} selected
        </span>
        {readOnly && (
          <span className="scientific-local-context" data-testid="scientific-local-context">
            Local selection only
          </span>
        )}
        <button
          type="button"
          aria-label="Show curve replicates"
          aria-pressed={showReplicates}
          onClick={() => setShowReplicates((value) => !value)}
        >
          Replicates {showReplicates ? 'shown' : 'hidden'}
        </button>
      </div>

      <div className="scientific-canvas-grid">
        <svg
          ref={svgRef}
          className="scientific-svg"
          data-testid="curve-selection-surface"
          viewBox={`0 0 ${curveView.width} ${curveView.height}`}
          role="img"
          aria-label="Interactive four parameter logistic curve"
          onPointerDown={beginBoxSelection}
          onPointerMove={updateBoxSelection}
          onPointerUp={finishBoxSelection}
        >
          <rect
            className="curve-hit-plane"
            x="0"
            y="0"
            width={curveView.width}
            height={curveView.height}
            fill="transparent"
          />
          <g className="scientific-grid-lines">
            {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
              const y = curveView.top + fraction * plotHeight;
              return (
                <line
                  key={fraction}
                  x1={curveView.left}
                  x2={curveView.width - curveView.right}
                  y1={y}
                  y2={y}
                />
              );
            })}
          </g>
          <line
            className="scientific-axis"
            x1={curveView.left}
            x2={curveView.left}
            y1={curveView.top}
            y2={curveView.height - curveView.bottom}
          />
          <line
            className="scientific-axis"
            x1={curveView.left}
            x2={curveView.width - curveView.right}
            y1={curveView.height - curveView.bottom}
            y2={curveView.height - curveView.bottom}
          />
          <polyline
            className="curve-fit curve-fit-committed"
            points={curveGeometryPath(
              document.derived.curve_geometry,
              scaleX,
              scaleY,
            )}
          />
          <polygon
            className="curve-confidence-band"
            data-testid="curve-confidence-band"
            points={curveConfidenceBandPath(
              document.derived.response_confidence_band_95.samples,
              scaleX,
              scaleY,
            )}
            aria-label={`SciPy covariance ${Math.round(
              document.derived.response_confidence_band_95.confidence_level * 100,
            )}% response confidence band`}
          >
            <title>
              SciPy covariance-derived {Math.round(
                document.derived.response_confidence_band_95.confidence_level * 100,
              )}
              % response confidence band
            </title>
          </polygon>
          {previewDocument && (
            <>
              <polygon
                className="curve-confidence-band curve-confidence-band-preview"
                data-testid="curve-preview-confidence-band"
                points={curveConfidenceBandPath(
                  previewDocument.derived.response_confidence_band_95.samples,
                  scaleX,
                  scaleY,
                )}
                aria-label="Preview SciPy covariance response confidence band"
              />
              <polyline
                className="curve-fit curve-fit-preview"
                data-testid="curve-preview-fit"
                points={curveGeometryPath(
                  previewDocument.derived.curve_geometry,
                  scaleX,
                  scaleY,
                )}
              />
            </>
          )}
          {points.map((point) => {
            const excluded = committedExcluded.has(point.id);
            const selected = selectedSet.has(point.id);
            return (
              <circle
                key={point.id}
                data-testid={`curve-point-${point.id}`}
                className={`curve-point ${selected ? 'curve-point-selected' : ''} ${
                  excluded ? 'curve-point-excluded' : ''
                }`}
                cx={scaleX(point.dose_nm)}
                cy={scaleY(point.response_percent)}
                r={selected ? 6 : 4.5}
                fill={seriesColor(series, point.series_id ?? 'series')}
                role="button"
                tabIndex={0}
                aria-label={`Curve point ${point.id}, dose ${point.dose_nm} nanomolar, response ${point.response_percent} percent${excluded ? ', excluded' : ''}`}
                aria-pressed={selected}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  selectPoint(
                    point.id,
                    event.ctrlKey || event.metaKey || event.shiftKey,
                  );
                }}
                onKeyDown={(event) =>
                  activateOnKeyboard(event, () =>
                    selectPoint(point.id, event.shiftKey),
                  )
                }
                onPointerEnter={() => setHoveredPoint(point)}
                onPointerLeave={() => setHoveredPoint(undefined)}
              >
                <title>
                  {point.id}: {point.dose_nm} nM / {point.response_percent}%
                  {excluded ? ' (excluded)' : ''}
                </title>
              </circle>
            );
          })}
          {selectionBox && (
            <rect
              className="curve-selection-box"
              x={Math.min(selectionBox.start.x, selectionBox.current.x)}
              y={Math.min(selectionBox.start.y, selectionBox.current.y)}
              width={Math.abs(selectionBox.start.x - selectionBox.current.x)}
              height={Math.abs(selectionBox.start.y - selectionBox.current.y)}
            />
          )}
          <text
            className="scientific-axis-label"
            x={curveView.width / 2}
            y={curveView.height - 9}
            textAnchor="middle"
          >
            Dose ({document.source.concentration.unit}, {xMapping.output_unit})
          </text>
          <text
            className="scientific-axis-label"
            x={17}
            y={curveView.height / 2}
            textAnchor="middle"
            transform={`rotate(-90 17 ${curveView.height / 2})`}
          >
            Response (%)
          </text>
        </svg>

        <aside className="scientific-metrics" aria-label="Curve metrics">
          <MetricValue label="Bottom" value={activeFit.bottom} />
          <MetricValue label="Top" value={activeFit.top} />
          <MetricValue label="IC50 (nM)" value={activeFit.ic50_nm} />
          <MetricValue label="Hill slope" value={activeFit.hill_slope} />
          <MetricValue
            label="Response band"
            value={`${Math.round(
              (previewDocument ?? document).derived.response_confidence_band_95
                .confidence_level * 100,
            )}% SciPy covariance`}
          />
          <MetricValue
            label="R squared"
            value={
              (previewDocument ?? document).derived.goodness_of_fit?.r_squared ??
              'unknown'
            }
          />
          <MetricValue
            label="RMSE"
            value={
              (previewDocument ?? document).derived.goodness_of_fit?.rmse ??
              'unknown'
            }
          />
          <div className="scientific-hover" data-testid="scientific-hover-detail">
            <span>Hover</span>
            <strong>{hoveredPoint?.id ?? 'No point'}</strong>
            <small>
              {hoveredPoint
                ? `${hoveredPoint.dose_nm} nM / ${hoveredPoint.response_percent}%`
                : 'Point details appear here.'}
            </small>
          </div>
        </aside>
      </div>

      {previewDocument && (
        <OperationPreview
          title="Curve refit preview"
          summary={`${previewDocument.state.excluded_point_ids.length} excluded / ${previewDocument.data.points.length} descriptor points`}
          proposedBy={operation?.agent_id}
          geometry={operation?.preview_geometry}
          applying={applying}
          applyDisabled={!onApply}
          onApply={() => void applyPreview()}
          onDismiss={() => {
            setDismissedOperation(operation);
            setApplyError(undefined);
          }}
        />
      )}
      {applyError && (
        <p className="scientific-operation-error" role="alert">
          {applyError}
        </p>
      )}
    </section>
  );
}

const flowView = {
  width: 720,
  height: 330,
  left: 58,
  right: 24,
  top: 24,
  bottom: 44,
};

const flowTransforms = [
  { value: 'linear', label: 'Linear' },
  {
    value: 'asinh-10000-4.5-0',
    label: 'Asinh (T=10000, M=4.5, A=0)',
  },
] as const;

type FlowDrag =
  | {
      kind: 'vertex';
      index: number;
    }
  | {
      kind: 'move';
      start: { x: number; y: number };
      points: Array<{ x: number; y: number }>;
    };

type FlowDraftGate = Omit<FlowGate, 'geometry'> & {
  geometry: Omit<FlowGate['geometry'], 'vertices'> & {
    vertices: Array<{ x: number; y: number }>;
  };
};

function FlowStudio({
  artifactId,
  document,
  readOnly = false,
  selection,
  operation,
  onSelectionChange,
  onApply,
}: Omit<ScientificArtifactStudioProps, 'document'> & {
  document: FlowScientificArtifactDocument | LocalFlowScientificArtifactDocument;
}) {
  const canonicalDocument = isCanonicalFlowDocument(document)
    ? document
    : undefined;
  const svgRef = useRef<SVGSVGElement>(null);
  const initialSelectionGate =
    selection?.renderer === 'flow' &&
    selection.artifactId === artifactId &&
    selection.gateId
      ? document.state.gates.find((gate) => gate.id === selection.gateId)
      : undefined;
  const initialGate =
    initialSelectionGate ??
    document.state.gates.find(
      (gate) => gate.id === document.state.selected_gate_id,
    ) ?? document.state.gates[0];
  const channelIds = document.data.channels.length
    ? document.data.channels.map((channel) => channel.id)
    : Object.keys(document.data.sampled_events[0]?.values ?? {});
  const [xChannel, setXChannel] = useState(
    initialGate?.axes[0] ?? document.state.x_channel ?? channelIds[0] ?? '',
  );
  const [yChannel, setYChannel] = useState(
    initialGate?.axes[1] ?? document.state.y_channel ?? channelIds[1] ?? channelIds[0] ?? '',
  );
  const [transform, setTransform] = useState(
    initialGate?.transform ?? document.state.transform,
  );
  const [selectedGateId, setSelectedGateId] = useState<string | undefined>(
    initialGate?.id,
  );
  const [hasExplicitGateSelection, setHasExplicitGateSelection] = useState(
    Boolean(initialSelectionGate),
  );
  const [draftGate, setDraftGate] = useState<FlowDraftGate>();
  const [drag, setDrag] = useState<FlowDrag>();
  const [hoveredEvent, setHoveredEvent] = useState<FlowEvent>();
  const [dismissedOperation, setDismissedOperation] =
    useState<ScientificOperationResult>();
  const [applyError, setApplyError] = useState<string>();
  const [applying, setApplying] = useState(false);
  const canonicalRevision = canonicalDocument?.revision;
  const canonicalStateKey = `${artifactId}:${canonicalRevision ?? 'local'}`;
  const previousCanonicalStateKey = useRef(canonicalStateKey);

  useEffect(() => {
    if (previousCanonicalStateKey.current === canonicalStateKey) return;
    previousCanonicalStateKey.current = canonicalStateKey;
    const gate =
      document.state.gates.find(
        (candidate) => candidate.id === document.state.selected_gate_id,
      ) ?? document.state.gates[0];
    const channels = document.data.channels.length
      ? document.data.channels.map((channel) => channel.id)
      : Object.keys(document.data.sampled_events[0]?.values ?? {});
    setXChannel(gate?.axes[0] ?? document.state.x_channel ?? channels[0] ?? '');
    setYChannel(gate?.axes[1] ?? document.state.y_channel ?? channels[1] ?? channels[0] ?? '');
    setTransform(gate?.transform ?? document.state.transform);
    setSelectedGateId(gate?.id);
    setHasExplicitGateSelection(false);
    setDraftGate(undefined);
    setDismissedOperation(undefined);
    setApplyError(undefined);
    // CAS revision is the canonical reset boundary. Metadata refreshes must not
    // discard an uncommitted human gate draft.
  }, [canonicalStateKey]);

  const xValues = document.data.sampled_events.map(
    (event) => transformFlowValue(event.values[xChannel] ?? 0, transform),
  );
  const yValues = document.data.sampled_events.map(
    (event) => transformFlowValue(event.values[yChannel] ?? 0, transform),
  );
  const xMin = Math.min(...xValues, 0);
  const xMax = Math.max(...xValues, 1);
  const yMin = Math.min(...yValues, 0);
  const yMax = Math.max(...yValues, 1);
  const plotWidth = flowView.width - flowView.left - flowView.right;
  const plotHeight = flowView.height - flowView.top - flowView.bottom;
  const scaleX = (value: number) =>
    flowView.left + ((value - xMin) / Math.max(1, xMax - xMin)) * plotWidth;
  const scaleY = (value: number) =>
    flowView.top + ((yMax - value) / Math.max(1, yMax - yMin)) * plotHeight;
  const unscaleX = (value: number) =>
    xMin + ((value - flowView.left) / plotWidth) * (xMax - xMin);
  const unscaleY = (value: number) =>
    yMax - ((value - flowView.top) / plotHeight) * (yMax - yMin);
  const activeGate =
    draftGate ??
    document.state.gates.find((gate) => gate.id === selectedGateId);
  const selectedEvents = activeGate
    ? document.data.sampled_events.filter((event) =>
        pointInPolygon(
          {
            x: transformFlowValue(event.values[xChannel] ?? 0, transform),
            y: transformFlowValue(event.values[yChannel] ?? 0, transform),
          },
          activeGate.geometry.vertices.map(flowPoint),
        ),
      )
    : [];
  const previewDocument: FlowScientificArtifactDocument | undefined =
    !readOnly &&
    canonicalDocument &&
    operation?.renderer === 'flow' &&
    operation.operation === 'flow.adjust_selected_gate' &&
    operation.selection.artifactId === artifactId &&
    operation.base_revision === canonicalRevision &&
    operation.selection.gateId === activeGate?.id &&
    operation !== dismissedOperation
      ? mergeScientificOperation(canonicalDocument, operation)
      : undefined;
  const previewGate =
    operation?.preview_geometry?.type === 'polygon' && previewDocument
      ? {
          id: operation.selection.gateId ?? selectedGateId ?? 'preview-gate',
          name: 'Agent preview',
          geometry: {
            kind: 'polygon',
            vertices: operation.preview_geometry.points.map(
              (point) => [point.x, point.y] as [number, number],
            ),
          },
          axes: [xChannel, yChannel] as [string, string],
        }
      : previewDocument?.state.gates.find(
          (gate) => gate.id === previewDocument.state.selected_gate_id,
        );
  const flowSelection: ScientificSelectionBase = {
    renderer: 'flow',
    artifactId,
    ids: [],
    gateId: hasExplicitGateSelection ? activeGate?.id : undefined,
    channels: { x: xChannel, y: yChannel },
    geometry: hasExplicitGateSelection && activeGate
      ? {
          type: 'polygon',
          points: activeGate.geometry.vertices.map((vertex) => ({
            x: round(flowPoint(vertex).x),
            y: round(flowPoint(vertex).y),
          })),
        }
      : undefined,
    gate: hasExplicitGateSelection && activeGate
      ? flowGateToRaw(activeGate, [xChannel, yChannel])
      : undefined,
    axes: [xChannel, yChannel],
    transform,
  };
  const flowStats = summarizeFlowSelection(selectedEvents, xChannel, yChannel);

  useEffect(() => {
    onSelectionChange(flowSelection);
  }, [
    activeGate?.id,
    hasExplicitGateSelection,
    JSON.stringify(activeGate?.geometry.vertices),
    artifactId,
    onSelectionChange,
    xChannel,
    yChannel,
    transform,
  ]);

  const selectGate = (gate: FlowGate) => {
    setXChannel(gate.axes[0]);
    setYChannel(gate.axes[1]);
    setTransform(gate.transform ?? document.state.transform);
    setSelectedGateId(gate.id);
    setHasExplicitGateSelection(true);
    setDraftGate(undefined);
    setApplyError(undefined);
  };

  const createGate = () => {
    const gate = {
      id: `gate-${(canonicalRevision ?? 0) + 1}`,
      name: `Gate ${document.state.gates.length + 1}`,
      parent_population: 'root',
      geometry: {
        kind: 'polygon' as const,
        vertices: [
          { x: interpolate(xMin, xMax, 0.32), y: interpolate(yMin, yMax, 0.32) },
          { x: interpolate(xMin, xMax, 0.68), y: interpolate(yMin, yMax, 0.32) },
          { x: interpolate(xMin, xMax, 0.68), y: interpolate(yMin, yMax, 0.68) },
          { x: interpolate(xMin, xMax, 0.32), y: interpolate(yMin, yMax, 0.68) },
        ],
      },
      axes: [xChannel, yChannel] as [string, string],
      transform,
    };
    setDraftGate(gate);
    setSelectedGateId(gate.id);
    setHasExplicitGateSelection(true);
    setApplyError(undefined);
  };

  const editGate = () => {
    const gate = document.state.gates.find((candidate) => candidate.id === selectedGateId);
    if (gate) {
      setDraftGate({
        ...gate,
        geometry: {
          ...gate.geometry,
          vertices: gate.geometry.vertices.map(flowPoint),
        },
      });
      setApplyError(undefined);
    }
  };

  const beginVertexDrag = (
    event: ReactPointerEvent<SVGCircleElement>,
    index: number,
  ) => {
    event.stopPropagation();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Some browser automation dispatches a pointer event without an active capture.
    }
    setDrag({ kind: 'vertex', index });
  };

  const beginGateMove = (
    event: ReactPointerEvent<SVGPolygonElement>,
    gate: FlowDraftGate,
  ) => {
    if (!draftGate || draftGate.id !== gate.id) return;
    event.stopPropagation();
    const point = svgPoint(event, svgRef.current, flowView.width, flowView.height);
    if (!point) return;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Keep local dragging usable when pointer capture is unavailable.
    }
    setDrag({
      kind: 'move',
      start: { x: unscaleX(point.x), y: unscaleY(point.y) },
      points: gate.geometry.vertices.map((gatePoint) => ({ ...gatePoint })),
    });
  };

  const updateGateDrag = (event: ReactPointerEvent<SVGElement>) => {
    if (!drag || !draftGate) return;
    const point = svgPoint(event, svgRef.current, flowView.width, flowView.height);
    if (!point) return;
    const dataPoint = { x: unscaleX(point.x), y: unscaleY(point.y) };
    if (drag.kind === 'vertex') {
      setDraftGate({
        ...draftGate,
        geometry: {
          ...draftGate.geometry,
          vertices: draftGate.geometry.vertices.map((gatePoint, index) =>
            index === drag.index ? dataPoint : gatePoint,
          ),
        },
      });
      return;
    }
    const dx = dataPoint.x - drag.start.x;
    const dy = dataPoint.y - drag.start.y;
    setDraftGate({
      ...draftGate,
      geometry: {
        ...draftGate.geometry,
        vertices: drag.points.map((gatePoint) => ({
          x: gatePoint.x + dx,
          y: gatePoint.y + dy,
        })),
      },
    });
  };

  const applyGate = async () => {
    if (!previewDocument || !onApply) return;
    setApplying(true);
    setApplyError(undefined);
    try {
      if (canonicalRevision === undefined) return;
      await onApply(artifactId, canonicalRevision, previewDocument);
      setDraftGate(undefined);
      setDismissedOperation(operation);
    } catch (cause) {
      setApplyError(cause instanceof Error ? cause.message : 'Flow gate update failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <section
      className="scientific-studio flow-studio"
      data-testid="scientific-renderer-flow"
      data-artifact-revision={canonicalRevision}
      data-local-context={readOnly ? 'true' : 'false'}
      data-committed-state={`gates:${document.state.gates.length}`}
      aria-label="Flow Studio scientific renderer"
    >
      <div className="scientific-toolbar flow-toolbar">
        <div>
          <span className="scientific-kicker">Flow Studio / cytometry</span>
          <strong>{document.title ?? 'Flow cytometry sample'}</strong>
        </div>
        <label>
          <span>X channel</span>
          <select
            aria-label="Flow X channel"
            value={xChannel}
            onChange={(event) => {
              setXChannel(event.target.value);
              setDraftGate(undefined);
              setSelectedGateId(undefined);
              setHasExplicitGateSelection(false);
            }}
          >
            {channelIds.map((channel) => (
              <option key={channel} value={channel}>
                {channel}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Y channel</span>
          <select
            aria-label="Flow Y channel"
            value={yChannel}
            onChange={(event) => {
              setYChannel(event.target.value);
              setDraftGate(undefined);
              setSelectedGateId(undefined);
              setHasExplicitGateSelection(false);
            }}
          >
            {channelIds.map((channel) => (
              <option key={channel} value={channel}>
                {channel}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Transform</span>
          <select
            aria-label="Flow transform"
            value={transform}
            onChange={(event) => {
              setTransform(event.target.value);
              setDraftGate(undefined);
              setSelectedGateId(undefined);
              setHasExplicitGateSelection(false);
            }}
          >
            {flowTransforms.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <span
          className="scientific-selection-count"
          data-testid="scientific-selection-count"
          aria-live="polite"
        >
          {selectedEvents.length} selected
        </span>
        {readOnly && (
          <span className="scientific-local-context" data-testid="scientific-local-context">
            Local selection only
          </span>
        )}
        {!readOnly && (
          <>
            <button type="button" aria-label="Create polygon gate" onClick={createGate}>
              New gate
            </button>
            <button
              type="button"
              aria-label="Edit selected polygon gate"
              disabled={!selectedGateId || Boolean(draftGate)}
              onClick={editGate}
            >
              Edit gate
            </button>
          </>
        )}
      </div>

      <div className="scientific-canvas-grid">
        <svg
          ref={svgRef}
          className="scientific-svg"
          data-testid="flow-selection-surface"
          viewBox={`0 0 ${flowView.width} ${flowView.height}`}
          role="img"
          aria-label="Interactive flow cytometry scatter plot"
          onPointerMove={updateGateDrag}
          onPointerUp={() => setDrag(undefined)}
        >
          <rect
            x={flowView.left}
            y={flowView.top}
            width={plotWidth}
            height={plotHeight}
            className="flow-plot-background"
          />
          <g className="scientific-grid-lines">
            {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
              <g key={fraction}>
                <line
                  x1={flowView.left}
                  x2={flowView.width - flowView.right}
                  y1={flowView.top + fraction * plotHeight}
                  y2={flowView.top + fraction * plotHeight}
                />
                <line
                  x1={flowView.left + fraction * plotWidth}
                  x2={flowView.left + fraction * plotWidth}
                  y1={flowView.top}
                  y2={flowView.height - flowView.bottom}
                />
              </g>
            ))}
          </g>
          {document.data.sampled_events.map((event) => {
            const selected = selectedEvents.some((candidate) => candidate.id === event.id);
            return (
              <circle
                key={event.id}
                data-testid={`flow-event-${event.id}`}
                className={`flow-event ${selected ? 'flow-event-selected' : ''}`}
                cx={scaleX(transformFlowValue(event.values[xChannel] ?? 0, transform))}
                cy={scaleY(transformFlowValue(event.values[yChannel] ?? 0, transform))}
                r={selected ? 3.5 : 2.3}
                onPointerEnter={() => setHoveredEvent(event)}
                onPointerLeave={() => setHoveredEvent(undefined)}
              >
                <title>
                  {event.id}: {xChannel}=
                  {formatNumber(transformFlowValue(event.values[xChannel] ?? 0, transform))},{' '}
                  {yChannel}=
                  {formatNumber(transformFlowValue(event.values[yChannel] ?? 0, transform))}
                </title>
              </circle>
            );
          })}
          {document.state.gates.map((gate) => {
            if (draftGate?.id === gate.id) return null;
            if (
              gate.axes[0] !== xChannel ||
              gate.axes[1] !== yChannel ||
              (gate.transform ?? document.state.transform) !== transform
            ) {
              return null;
            }
            const selected = gate.id === selectedGateId;
            return (
              <polygon
                key={gate.id}
                data-testid={`flow-gate-${gate.id}`}
                className={`flow-gate ${selected ? 'flow-gate-selected' : ''}`}
                points={gate.geometry.vertices
                  .map((point) => `${scaleX(point[0])},${scaleY(point[1])}`)
                  .join(' ')}
                role="button"
                tabIndex={0}
                aria-label={`Select polygon gate ${gate.name}`}
                aria-pressed={selected}
                onClick={() => selectGate(gate)}
                onKeyDown={(event) => activateOnKeyboard(event, () => selectGate(gate))}
              >
                <title>{gate.name}</title>
              </polygon>
            );
          })}
          {draftGate && (
            <g data-testid="flow-draft-gate">
              <polygon
                data-testid={`flow-gate-${draftGate.id}`}
                className="flow-gate flow-gate-draft"
                points={draftGate.geometry.vertices
                  .map((point) => `${scaleX(point.x)},${scaleY(point.y)}`)
                  .join(' ')}
                role="button"
                tabIndex={0}
                aria-label={`Move polygon gate ${draftGate.name}`}
                onPointerDown={(event) => beginGateMove(event, draftGate)}
                onPointerMove={(event) => {
                  event.stopPropagation();
                  updateGateDrag(event);
                }}
                onPointerUp={(event) => {
                  event.stopPropagation();
                  setDrag(undefined);
                }}
              />
              {draftGate.geometry.vertices.map((point, index) => (
                <circle
                  key={`${draftGate.id}-${index}`}
                  data-testid={`flow-gate-vertex-${index}`}
                  className="flow-gate-vertex"
                  cx={scaleX(point.x)}
                  cy={scaleY(point.y)}
                  r={5}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit gate vertex ${index + 1}`}
                  onPointerDown={(event) => beginVertexDrag(event, index)}
                  onPointerMove={(event) => {
                    event.stopPropagation();
                    updateGateDrag(event);
                  }}
                  onPointerUp={(event) => {
                    event.stopPropagation();
                    setDrag(undefined);
                  }}
                />
              ))}
            </g>
          )}
          {previewGate && (
            <polygon
              data-testid="flow-preview-gate"
              className="flow-gate flow-gate-preview"
              points={previewGate.geometry.vertices
                .map((point) => `${scaleX(point[0])},${scaleY(point[1])}`)
                .join(' ')}
              aria-label="Agent polygon gate preview"
            />
          )}
          <text
            className="scientific-axis-label"
            x={flowView.width / 2}
            y={flowView.height - 9}
            textAnchor="middle"
          >
            {xChannel}
          </text>
          <text
            className="scientific-axis-label"
            x={17}
            y={flowView.height / 2}
            textAnchor="middle"
            transform={`rotate(-90 17 ${flowView.height / 2})`}
          >
            {yChannel}
          </text>
        </svg>

        <aside className="scientific-metrics" aria-label="Flow selection statistics">
          <MetricValue label="Sampled events" value={document.data.sampled_events.length} />
          <MetricValue label="Sample selected" value={selectedEvents.length} />
          <MetricValue
            label="Sample percentage"
            value={`${round(
              (selectedEvents.length /
                Math.max(1, document.data.sampled_events.length)) *
                100,
            )}%`}
          />
          <MetricValue
            label="Full FCS selected"
            value={
              flowPopulation(previewDocument ?? document, activeGate?.id)?.event_count ??
              'unknown'
            }
          />
          <MetricValue
            label="Full FCS total"
            value={document.data.event_count}
          />
          <MetricValue
            label="Full FCS percentage"
            value={
              flowPopulation(previewDocument ?? document, activeGate?.id)
                ? `${formatNumber(
                    flowPopulation(previewDocument ?? document, activeGate?.id)!
                      .percentage,
                  )}%`
                : 'unknown'
            }
          />
          <MetricValue
            label={`Median ${xChannel}`}
            value={
              flowPopulation(previewDocument ?? document, activeGate?.id)?.statistics[
                xChannel
              ]?.median ?? flowStats.meanX
            }
          />
          <MetricValue
            label={`Median ${yChannel}`}
            value={
              flowPopulation(previewDocument ?? document, activeGate?.id)?.statistics[
                yChannel
              ]?.median ?? flowStats.meanY
            }
          />
          <div className="scientific-hover" data-testid="scientific-hover-detail">
            <span>Hover</span>
            <strong>{hoveredEvent?.id ?? 'No event'}</strong>
            <small>
              {hoveredEvent
                ? `${xChannel} ${formatNumber(
                    transformFlowValue(hoveredEvent.values[xChannel] ?? 0, transform),
                  )} / ${yChannel} ${formatNumber(
                    transformFlowValue(hoveredEvent.values[yChannel] ?? 0, transform),
                  )}`
                : 'Event details appear here.'}
            </small>
          </div>
        </aside>
      </div>

      {draftGate && !previewDocument && (
        <div className="scientific-local-draft" data-testid="scientific-local-draft">
          Local gate draft. Use the Artifact composer to compute the full FCS operation before Apply.
        </div>
      )}
      {previewDocument && (
        <OperationPreview
          title="Polygon gate preview"
          summary={flowOperationSummary(previewDocument, operation)}
          proposedBy={operation?.agent_id}
          geometry={operation?.preview_geometry}
          applying={applying}
          applyDisabled={!onApply}
          onApply={() => void applyGate()}
          onDismiss={() => {
            setDismissedOperation(operation);
            setApplyError(undefined);
          }}
        />
      )}
      {applyError && (
        <p className="scientific-operation-error" role="alert">
          {applyError}
        </p>
      )}
    </section>
  );
}

function OperationPreview({
  title,
  summary,
  proposedBy,
  geometry,
  applying,
  applyDisabled,
  onApply,
  onDismiss,
}: {
  title: string;
  summary: string;
  proposedBy?: string;
  geometry: ScientificSelectionBase['geometry'];
  applying: boolean;
  applyDisabled: boolean;
  onApply(): void;
  onDismiss(): void;
}) {
  return (
    <div
      className="scientific-operation-preview"
      data-testid="scientific-operation-preview"
      data-preview-geometry={geometry ? JSON.stringify(geometry) : ''}
      role="region"
      aria-label={`AI proposed ${title}`}
    >
      <span>
        <strong>{title}</strong>
        <small>{summary}</small>
      </span>
      <span className="scientific-operation-note">
        {proposedBy ? `Proposed by ${proposedBy}. ` : ''}
        Transient until Human Apply
      </span>
      <button
        data-testid="operation-dismiss"
        type="button"
        aria-label="Dismiss scientific operation preview"
        onClick={onDismiss}
        disabled={applying}
      >
        Dismiss
      </button>
      <button
        data-testid="operation-apply"
        type="button"
        aria-label="Apply scientific operation"
        onClick={onApply}
        disabled={applying || applyDisabled}
      >
        {applying ? 'Applying…' : 'Apply'}
      </button>
    </div>
  );
}

function MetricValue({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div className="scientific-metric">
      <span>{label}</span>
      <strong>{typeof value === 'number' ? formatNumber(value) : value}</strong>
    </div>
  );
}

function firstPointPerConcentration(points: ScientificPoint[]) {
  const seen = new Set<string>();
  return points.filter((point) => {
    const key = `${point.series_id ?? 'series'}:${point.dose_nm}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function seriesColor(
  series: Array<{ id: string; color?: string }>,
  seriesId: string,
) {
  const explicit = series.find((item) => item.id === seriesId)?.color;
  if (explicit) return explicit;
  const index = Math.max(
    0,
    series.findIndex((item) => item.id === seriesId),
  );
  return [
    'var(--cadence-data-blue)',
    'var(--cadence-data-violet)',
    'var(--cadence-data-green)',
    'var(--cadence-data-ochre)',
    'var(--cadence-data-rose)',
    'var(--cadence-brand)',
  ][index % 6];
}

function curveGeometryPath(
  geometry: Array<{ dose_nm: number; response_percent: number }>,
  scaleX: (value: number) => number,
  scaleY: (value: number) => number,
) {
  return geometry
    .map(
      (point) =>
        `${round(scaleX(point.dose_nm))},${round(scaleY(point.response_percent))}`,
    )
    .join(' ');
}

function curveConfidenceBandPath(
  samples: Array<{
    dose_nm: number;
    lower_response_percent: number;
    upper_response_percent: number;
  }>,
  scaleX: (value: number) => number,
  scaleY: (value: number) => number,
) {
  return [
    ...samples.map(
      (sample) =>
        `${round(scaleX(sample.dose_nm))},${round(
          scaleY(sample.upper_response_percent),
        )}`,
    ),
    ...samples
      .slice()
      .reverse()
      .map(
        (sample) =>
          `${round(scaleX(sample.dose_nm))},${round(
            scaleY(sample.lower_response_percent),
          )}`,
      ),
  ].join(' ');
}

function curveDoseMappingValue(
  doseNm: number,
  mapping: CurveScientificArtifactDocument['source']['x_mapping'],
) {
  if (mapping.name === 'log10_concentration' && mapping.formula === 'log10(dose_nm)') {
    return Math.log10(doseNm);
  }
  throw new Error(`Unsupported Curve concentration mapping: ${mapping.name}`);
}

function curveDoseMappingInverse(
  mappedValue: number,
  mapping: CurveScientificArtifactDocument['source']['x_mapping'],
) {
  if (mapping.name === 'log10_concentration' && mapping.formula === 'log10(dose_nm)') {
    return 10 ** mappedValue;
  }
  throw new Error(`Unsupported Curve concentration mapping: ${mapping.name}`);
}

function mergeScientificOperation<T extends ScientificArtifactDocument>(
  document: T,
  operation: ScientificOperationResult,
): T {
  const statePatch = recordFrom(operation.after.state);
  const derivedPatch = recordFrom(operation.after.derived);
  return {
    ...document,
    state: deepMergeRecord(document.state, statePatch),
    derived: deepMergeRecord(document.derived, derivedPatch),
  } as T;
}

function flowOperationSummary(
  document: FlowScientificArtifactDocument,
  operation: ScientificOperationResult | undefined,
) {
  const gateId =
    operation?.selection.gateId ?? document.state.selected_gate_id;
  const population = gateId ? document.derived.populations?.[gateId] : undefined;
  return population
    ? `${population.event_count} full FCS events / ${formatNumber(population.percentage)}%`
    : 'Agent-computed full FCS gate result';
}

function curveSelectionPoints(points: ScientificPoint[], selectedIds: string[]) {
  const selected = new Set(selectedIds);
  return points.flatMap((point, index) => {
    if (!selected.has(point.id)) return [];
    return [
      {
        id: point.id,
        dose_nm: point.dose_nm,
        response_percent: point.response_percent,
        replicate: point.replicate,
        fitted_value: point.fitted_value,
        residual: point.residual,
        neighbors: [points[index - 1]?.id, points[index + 1]?.id].filter(Boolean),
      },
    ];
  });
}

function flowPoint(vertex: [number, number] | { x: number; y: number }) {
  return Array.isArray(vertex)
    ? { x: vertex[0], y: vertex[1] }
    : { x: vertex.x, y: vertex.y };
}

function flowGateToRaw(
  gate: FlowGate | FlowDraftGate,
  axes: [string, string] = gate.axes,
): FlowGate {
  return {
    ...gate,
    axes,
    geometry: {
      ...gate.geometry,
      vertices: gate.geometry.vertices.map((vertex) => {
        const point = flowPoint(vertex);
        return [round(point.x), round(point.y)];
      }),
    },
  };
}

function transformFlowValue(value: number, transform: string) {
  if (transform === 'linear') return value;
  if (transform === 'asinh-10000-4.5-0') {
    const decades = 4.5;
    const preScale = Math.sinh(decades * Math.log(10)) / 10000;
    return Math.asinh(value * preScale) / (decades * Math.log(10));
  }
  return value;
}

function flowPopulation(
  document: FlowScientificArtifactDocument | LocalFlowScientificArtifactDocument,
  gateId: string | undefined,
) {
  return gateId ? document.derived.populations?.[gateId] : undefined;
}

function recordFrom(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function deepMergeRecord(
  base: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...base };
  Object.entries(patch).forEach(([key, value]) => {
    const current = merged[key];
    merged[key] =
      isRecord(current) && isRecord(value)
        ? deepMergeRecord(current, value)
        : value;
  });
  return merged;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function sameIds(left: string[], right: string[]) {
  return (
    left.length === right.length &&
    [...left].sort().every((id, index) => id === [...right].sort()[index])
  );
}

function summarizeFlowSelection(
  events: FlowEvent[],
  xChannel: string,
  yChannel: string,
) {
  if (!events.length) return { meanX: 0, meanY: 0 };
  return {
    meanX: round(
      events.reduce((sum, event) => sum + (event.values[xChannel] ?? 0), 0) /
        events.length,
    ),
    meanY: round(
      events.reduce((sum, event) => sum + (event.values[yChannel] ?? 0), 0) /
        events.length,
    ),
  };
}

function pointInPolygon(
  point: { x: number; y: number },
  polygon: Array<{ x: number; y: number }>,
) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index];
    const previousPoint = polygon[previous];
    const crosses =
      currentPoint.y > point.y !== previousPoint.y > point.y &&
      point.x <
        ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
          (previousPoint.y - currentPoint.y || Number.EPSILON) +
          currentPoint.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function svgPoint(
  event: { clientX: number; clientY: number },
  svg: SVGSVGElement | null,
  width: number,
  height: number,
) {
  const rect = svg?.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) return undefined;
  return {
    x: ((event.clientX - rect.left) / rect.width) * width,
    y: ((event.clientY - rect.top) / rect.height) * height,
  };
}

function activateOnKeyboard(
  event: KeyboardEvent<SVGElement>,
  action: () => void,
) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  action();
}

function interpolate(min: number, max: number, fraction: number) {
  return min + (max - min) * fraction;
}

function round(value: number) {
  return Number(value.toFixed(3));
}

function formatNumber(value: number) {
  return Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
    : String(round(value));
}
